import React, { Component } from 'react';

class GlobalErrorHandler extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      errorType: null,
      errorMessage: null,
      errorDetails: null
    };

    // Handle global JavaScript errors
    window.addEventListener('error', this.handleGlobalError);
    window.addEventListener('unhandledrejection', this.handlePromiseRejection);
  }

  componentWillUnmount() {
    window.removeEventListener('error', this.handleGlobalError);
    window.removeEventListener('unhandledrejection', this.handlePromiseRejection);
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      errorType: 'component',
      errorMessage: error.message,
      errorDetails: error.stack
    };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Component Error:', error, errorInfo);
  }

  handleGlobalError = (event) => {
    console.error('Global Error:', event.error);
    this.setState({
      hasError: true,
      errorType: 'javascript',
      errorMessage: event.error?.message || 'An unexpected error occurred',
      errorDetails: event.error?.stack
    });
  };

  handlePromiseRejection = (event) => {
    console.error('Promise Rejection:', event.reason);
    this.setState({
      hasError: true,
      errorType: 'promise',
      errorMessage: event.reason?.message || 'A network or loading error occurred',
      errorDetails: event.reason
    });
  };

  getErrorInfo = () => {
    const { errorType, errorMessage } = this.state;

    // Categorize errors for user-friendly messaging
    if (errorMessage?.includes('Module not found') || errorMessage?.includes('Cannot resolve')) {
      return {
        title: 'Application Loading Issue',
        message: 'We\'re having trouble loading some application resources. This is usually temporary.',
        suggestion: 'Please refresh the page or clear your browser cache.',
        icon: '🔄',
        actions: ['refresh', 'clearCache']
      };
    }

    if (errorMessage?.includes('Network') || errorMessage?.includes('fetch')) {
      return {
        title: 'Connection Problem',
        message: 'Unable to connect to our servers. Please check your internet connection.',
        suggestion: 'Try refreshing the page or check your network connection.',
        icon: '🌐',
        actions: ['refresh', 'checkNetwork']
      };
    }

    if (errorMessage?.includes('is not a function') || errorMessage?.includes('undefined')) {
      return {
        title: 'Application Error',
        message: 'Something unexpected happened. Our team has been notified.',
        suggestion: 'Please try refreshing the page or contact support if the problem continues.',
        icon: '⚠️',
        actions: ['refresh', 'contact']
      };
    }

    if (errorMessage?.includes('ChunkLoadError') || errorMessage?.includes('Loading chunk')) {
      return {
        title: 'Loading Error',
        message: 'There was a problem loading the application. This often happens after updates.',
        suggestion: 'Please refresh the page to load the latest version.',
        icon: '🔄',
        actions: ['refresh', 'hardRefresh']
      };
    }

    // Default error
    return {
      title: 'Something Went Wrong',
      message: 'We encountered an unexpected issue. Don\'t worry - your data is safe.',
      suggestion: 'Please try refreshing the page or contact our support team.',
      icon: '🚨',
      actions: ['refresh', 'contact']
    };
  };

  handleAction = (action) => {
    switch (action) {
      case 'refresh':
        window.location.reload();
        break;
      case 'hardRefresh':
        window.location.reload(true);
        break;
      case 'clearCache':
        if ('caches' in window) {
          caches.keys().then(names => {
            names.forEach(name => caches.delete(name));
          }).then(() => window.location.reload());
        } else {
          window.location.reload();
        }
        break;
      case 'goHome':
        window.location.href = '/';
        break;
      case 'retry':
        this.setState({ hasError: false });
        break;
      default:
        break;
    }
  };

  render() {
    if (this.state.hasError) {
      const errorInfo = this.getErrorInfo();
      
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
            {/* Error Icon */}
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">{errorInfo.icon}</div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {errorInfo.title}
              </h1>
              <p className="text-gray-600 mb-4">
                {errorInfo.message}
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
                <p className="text-sm text-blue-800">
                  💡 {errorInfo.suggestion}
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              {errorInfo.actions.includes('refresh') && (
                <button
                  onClick={() => this.handleAction('refresh')}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
                >
                  🔄 Refresh Page
                </button>
              )}

              {errorInfo.actions.includes('hardRefresh') && (
                <button
                  onClick={() => this.handleAction('hardRefresh')}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
                >
                  🔄 Force Refresh
                </button>
              )}

              {errorInfo.actions.includes('clearCache') && (
                <button
                  onClick={() => this.handleAction('clearCache')}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
                >
                  🧹 Clear Cache & Refresh
                </button>
              )}

              {errorInfo.actions.includes('retry') && (
                <button
                  onClick={() => this.handleAction('retry')}
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
                >
                  🔄 Try Again
                </button>
              )}

              <button
                onClick={() => this.handleAction('goHome')}
                className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-4 rounded-lg transition-colors"
              >
                🏠 Go to Home
              </button>
            </div>

            {/* Contact Information */}
            <div className="text-center pt-6 border-t border-gray-200 mt-6">
              <p className="text-sm text-gray-500 mb-3">
                Still having issues?
              </p>
              <div className="space-y-2">
                <a 
                  href="mailto:support@axel.com" 
                  className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  📧 support@axel.com
                </a>
                <br />
                <a 
                  href="tel:+1-800-AXEL-HELP" 
                  className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  📞 1-800-AXEL-HELP
                </a>
              </div>
            </div>

            {/* Technical Details for Developers */}
            {process.env.NODE_ENV === 'development' && (
              <details className="mt-6 bg-gray-50 rounded-lg p-4">
                <summary className="cursor-pointer text-sm text-gray-600 font-medium">
                  🔧 Technical Details (Dev Mode)
                </summary>
                <div className="mt-2 space-y-2">
                  <div className="text-xs">
                    <strong>Type:</strong> {this.state.errorType}
                  </div>
                  <div className="text-xs">
                    <strong>Message:</strong> {this.state.errorMessage}
                  </div>
                  {this.state.errorDetails && (
                    <div className="text-xs">
                      <strong>Stack:</strong>
                      <pre className="mt-1 text-xs bg-gray-100 p-2 rounded overflow-auto max-h-40">
                        {typeof this.state.errorDetails === 'string' 
                          ? this.state.errorDetails 
                          : JSON.stringify(this.state.errorDetails, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default GlobalErrorHandler;
